import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import {
  Box,
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CodeMirror from "@uiw/react-codemirror";
import { sql } from "@codemirror/lang-sql";
import { EditorView } from "@codemirror/view";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import { useRbac } from "../../hooks/RbacContext";
import { useAuth } from "../../hooks/useAuth";
import { MainCard } from "../UserAccessManagement/UserAccessManagement.style";
import { ModalOverlay, ModalContainer } from "../../components/Modal/Modal.styled";
import Modal from "../../components/Modal/Modal";
import "../../components/SqlEditor/SqlEditor.css";

const GET_ALL_GENERATED_SQL_FEEDBACKS = gql`
  query GetAllGeneratedSqlFeedbacks($type: GeneratedSqlFeedbackType, $is_approved: Boolean) {
    GetAllGeneratedSqlFeedbacks(type: $type, is_approved: $is_approved) {
      generated_sql_feedback_id
      question
      description
      generated_sql
      expected_sql
      type
      feedback
      comment
      model_id
      raised_by
      is_approved
      approved_by
      approved_on
      created_on
      updated_on
    }
  }
`;

const UPDATE_GENERATED_SQL_FEEDBACK = gql`
  mutation UpdateGeneratedSqlFeedback($input: UpdateGeneratedSqlFeedbackInput!) {
    UpdateGeneratedSqlFeedback(input: $input) {
      generated_sql_feedback_id
      is_approved
      approved_by
      approved_on
      expected_sql
    }
  }
`;

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ReviewSql = () => {
  const { checkAccess } = useRbac();
  const { user } = useAuth();
  const { data, loading, error, refetch } = useQuery(GET_ALL_GENERATED_SQL_FEEDBACKS, {
    fetchPolicy: "cache-and-network",
  });
  const [updateFeedback, { loading: approveLoading }] = useMutation(UPDATE_GENERATED_SQL_FEEDBACK);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [expectedSqlEdit, setExpectedSqlEdit] = useState("");
  const [descriptionEdit, setDescriptionEdit] = useState("");
  const [successModal, setSuccessModal] = useState(null);

  const isQuestionType = (type) => type === "query" || type === "correction";

  const rawFeedbacks = data?.GetAllGeneratedSqlFeedbacks ?? [];
  const feedbacks = rawFeedbacks.map((row) => ({
    ...row,
    id: row.generated_sql_feedback_id ?? row.id,
  }));

  const openDetailModal = (row) => {
    setSelectedFeedback(row);
    setExpectedSqlEdit(row.expected_sql ?? row.generated_sql ?? "");
    setDescriptionEdit(row.description ?? "");
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedFeedback(null);
    setExpectedSqlEdit("");
    setDescriptionEdit("");
  };

  const handleApprove = async () => {
    if (!selectedFeedback?.generated_sql_feedback_id) return;
    try {
      const userId = user?.userDetails?.user_id ?? null;
      const input = {
        generated_sql_feedback_id: Number(selectedFeedback.generated_sql_feedback_id),
        is_approved: true,
        expected_sql: expectedSqlEdit.trim() || null,
        ...(userId != null && { approved_by: String(userId) }),
        approved_on: new Date().toISOString(),
      };
      if (isQuestionType(selectedFeedback.type)) {
        input.question = selectedFeedback.question ?? null;
      } else {
        input.description = descriptionEdit.trim() || null;
      }
      await updateFeedback({ variables: { input } });
      await refetch();
      closeDetailModal();
      setSuccessModal({ type: "success", title: "Success", message: "Feedback approved successfully." });
    } catch (err) {
      setSuccessModal({ type: "error", title: "Error", message: err?.message ?? "Failed to approve." });
    }
  };

  const getActions = (row) => [
    {
      icon: <VisibilityIcon fontSize="small" />,
      label: "Review",
      onClick: () => openDetailModal(row),
      sx: {
        color: "rgb(44, 52, 123)",
        "&:hover": { backgroundColor: "rgba(44, 52, 123, 0.08)" },
      },
    },
  ];

  const fixedColumns = { left: [], right: ["is_approved", "actions"] };

  const columns = [
    {
      id: "question",
      label: "Question",
      align: "left",
      render: (value) => (
        <Box sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>
          {value || "—"}
        </Box>
      ),
    },
    {
      id: "description",
      label: "Description",
      align: "left",
      render: (value) => (
        <Box sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>
          {value || "—"}
        </Box>
      ),
    },
    {
      id: "generated_sql",
      label: "Generated SQL",
      align: "left",
      render: (value) => (
        <Box sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>
          {value || "—"}
        </Box>
      ),
    },
    {
      id: "expected_sql",
      label: "Expected SQL",
      align: "left",
      render: (value) => (
        <Box sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>
          {value || "—"}
        </Box>
      ),
    },
    { id: "type", label: "Type", align: "left" },
    {
      id: "feedback",
      label: "Feedback",
      align: "left",
      render: (value) => {
        const isGood = value === "good";
        const isPoor = value === "poor";
        if (!isGood && !isPoor) return "—";
        return (
          <Chip
            label={isGood ? "Good" : "Poor"}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 500,
              backgroundColor: isGood ? "#E8F5E9" : "#FFF3E0",
              color: isGood ? "#2E7D32" : "#EF6C00",
              "& .MuiChip-label": { px: 1.5 },
            }}
          />
        );
      },
      searchValue: (value) => (value === "good" ? "good" : value === "poor" ? "poor" : ""),
    },
    {
      id: "model_id",
      label: "Model",
      align: "left",
      render: (value) => (
        <Box sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>
          {value || "—"}
        </Box>
      ),
    },
    { id: "raised_by", label: "Raised By", align: "left" },
    {
      id: "is_approved",
      label: "Status",
      align: "left",
      render: (value) => (
        <Chip
          label={value ? "Approved" : "Pending"}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
            color: value ? "#2E7D32" : "#EF6C00",
            "& .MuiChip-label": { px: 1.5 },
          }}
        />
      ),
      searchValue: (value) => (value ? "approved" : "pending"),
    },
    { id: "approved_by", label: "Approved By", align: "left" },
    { id: "approved_on", label: "Approved On", align: "left", render: formatDate },
    { id: "created_on", label: "Created On", align: "left", render: formatDate },
    { id: "updated_on", label: "Updated On", align: "left", render: formatDate },
  ];

  if (!checkAccess("reviewSql", "view")) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You don&apos;t have permission to view Review SQL.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <MainCard sx={{ p: 1.5 }}>
        <Alert severity="error" sx={{ borderRadius: 8 }}>
          {error.message}
        </Alert>
      </MainCard>
    );
  }

  if (loading) {
    return (
      <MainCard sx={{ height: "calc(100vh - 72px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={40} sx={{ color: "rgb(44, 52, 123)" }} />
          <Typography variant="body2" sx={{ color: "#64748b" }}>Loading feedbacks…</Typography>
        </Box>
      </MainCard>
    );
  }

  return (
    <>
      <MainCard sx={{ height: "calc(100vh - 72px)", maxHeight: "calc(100vh - 72px)" }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <CustomTable
            columns={columns}
            data={feedbacks}
            showActions
            showSearch
            showColumnVisibility={false}
            showAddButton={false}
            pageSize={10}
            stickyHeader
            fixedColumns={fixedColumns}
            actions={getActions}
            title="Review SQL"
            subtitle="Review and approve generated SQL feedbacks"
            alignment="left"
            fontSize="12px"
            showFilters
            compact={true}
          />
        </Box>
      </MainCard>

      {/* Detail modal: question, generated_sql, expected_sql, Approve */}
      {detailModalOpen && selectedFeedback && (
        <ModalOverlay onClick={closeDetailModal}>
          <ModalContainer
            onClick={(e) => e.stopPropagation()}
            sx={{ maxWidth: 720, width: "90%", padding: 0, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                Review feedback
              </Typography>
              <IconButton size="small" onClick={closeDetailModal} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
              {isQuestionType(selectedFeedback.type) ? (
                <Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Question
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {selectedFeedback.question || "—"}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Description
                  </Typography>
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={descriptionEdit}
                    onChange={(e) => setDescriptionEdit(e.target.value)}
                    placeholder="Edit description..."
                    fullWidth
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Generated SQL{selectedFeedback.model_id ? ` (${selectedFeedback.model_id})` : ""}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    fontSize: "0.8125rem",
                    fontFamily: "monospace",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 180,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {selectedFeedback.generated_sql || "—"}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Expected SQL
                </Typography>
                <Box
                  className="expected-sql-editor"
                  sx={{
                    mt: 0.5,
                    border: "1px solid #e2e8f0",
                    borderRadius: 1,
                    overflow: "hidden",
                    minHeight: 56,
                    maxHeight: 280,
                    backgroundColor: "#ffffff",
                    "& .cm-editor": {
                      fontSize: "0.8125rem",
                      minHeight: 56,
                      maxHeight: 280,
                      backgroundColor: "#ffffff",
                    },
                    "& .cm-theme-light": {
                      backgroundColor: "#ffffff !important",
                    },
                    "& .cm-gutters": {
                      backgroundColor: "#f8fafc !important",
                      color: "#64748b !important",
                      borderRight: "1px solid #e2e8f0 !important",
                    },
                    "& .cm-keyword": {
                      color: "#7c3aed !important",
                      fontWeight: "600 !important",
                    },
                    "& .cm-operator": { color: "#7c3aed !important" },
                    "& .cm-property": { color: "#1e293b !important" },
                    "& .cm-variable": { color: "#1e293b !important" },
                    "& .cm-scroller": { overflow: "auto" },
                    "& .cm-content": { padding: "12px 14px", lineHeight: 1.6 },
                  }}
                >
                  <CodeMirror
                    value={expectedSqlEdit}
                    onChange={setExpectedSqlEdit}
                    extensions={[sql(), EditorView.lineWrapping]}
                    placeholder="Edit expected SQL..."
                    theme="light"
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: true,
                      highlightActiveLine: true,
                      highlightSelectionMatches: true,
                      bracketMatching: true,
                      closeBrackets: true,
                      indentOnInput: true,
                    }}
                    height="56px"
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Comment
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: "#1e293b",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    minHeight: 24,
                  }}
                >
                  {selectedFeedback.comment || "—"}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2, py: 1.5, borderTop: "1px solid #e2e8f0", backgroundColor: "#fafafa" }}>
              <Button variant="outlined" size="small" onClick={closeDetailModal}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApprove}
                disabled={approveLoading}
                sx={{ backgroundColor: "rgb(44, 52, 123)", "&:hover": { backgroundColor: "rgb(35, 42, 100)" } }}
              >
                {approveLoading
                  ? (selectedFeedback?.is_approved ? "Updating…" : "Approving…")
                  : (selectedFeedback?.is_approved ? "Update" : "Approve")}
              </Button>
            </Box>
          </ModalContainer>
        </ModalOverlay>
      )}

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={() => setSuccessModal(null)}
        />
      )}
    </>
  );
};

export default ReviewSql;
